<script setup lang="ts">
import { KMessage, PageBannerType } from "@kaleidoscope-solutions/ks-design-system"
import { ExclamationMarkIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { storeToRefs } from "pinia"
import MissingInfoImage from "@/assets/pngs/MissingInfo.png?w=300&h=358&format=webp&imagetools"
import MatchTabs from "@/components/FileImport/Match/MatchTabs.vue"

import { useFileImportStore } from "@/stores"

const fileImportStore = useFileImportStore()

const { matchTabs, currentMatchTab, allItemsMatched } = storeToRefs(fileImportStore)

const tempMatchTabs = matchTabs.value.filter((tab) => tab.title !== "Channel")

await fileImportStore.populateMatchDataFromLocate()

function handleTabChange(selectedIndex: number) {
  fileImportStore.markCurrentMatchTabTouched()
  fileImportStore.currentMatchTab = selectedIndex
}

const currentAllIgnored = fileImportStore.currentCategoryAllIgnored

function closeEditBanner() {
  fileImportStore.editBanner = false
}
</script>

<template>
  <div class="flex w-full flex-1 flex-col overflow-auto">
    <KMessage
      v-if="currentAllIgnored"
      :type="PageBannerType.ERROR"
      title="Oops. Invalid import"
      class="pb-6"
      :closeable="false"
    >
      <p>All items are ignored, there will be no data to import.</p>
    </KMessage>

    <KMessage
      v-if="fileImportStore.editBanner"
      :type="PageBannerType.WARNING"
      :title="`Important. You have previously imported data for ${fileImportStore.editItems?.externalName} as ${fileImportStore.editItems?.previousName}.`"
      class="pb-6"
      @update:model-value="closeEditBanner"
    >
      <p>
        You will now import data for
        <b>{{ fileImportStore.editItems?.externalName }}</b> as
        <b>{{ fileImportStore.editItems?.newName }} </b>. This change will not
        affect previously imported data.
      </p>
    </KMessage>

    <div
      v-if="tempMatchTabs.length > 0"
      class="flex size-full flex-col gap-6 overflow-hidden"
    >
      <MatchTabs
        :tabs="tempMatchTabs"
        :selected-index="currentMatchTab"
        @change="handleTabChange"
      />
    </div>
    <div
      v-else
      class="flex w-full items-center justify-center bg-gray-100 p-4"
    >
      <div
        v-if="!allItemsMatched"
        class="my-2 flex min-h-96 items-center justify-center rounded-md bg-gray-100"
      >
        <div class="flex items-center">
          <div>
            <img
              :src="MissingInfoImage"
              class="w-52 px-8"
              alt=""
            >
          </div>
          <div class="w-80 space-y-4">
            <h2 class="text-lg font-bold">
              Nothing to match yet
            </h2>
            <p>
              Please provide the required information indicated with
              <ExclamationMarkIcon
                class="ml-2 inline size-4 text-red-400"
              />
            </p>
          </div>
        </div>
      </div>
      <span
        v-else
        class="text-lg font-bold"
      >No matches needed</span>
    </div>
  </div>
</template>
